"use client";

import React, { useState, useEffect } from "react";
import { Users, Calendar, Mail, ChevronDown, ChevronUp, Tag, Layers, Monitor } from "lucide-react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";

interface VolunteerMember {
    volunteer_id: string;
    name: string;
    email: string;
    opted_in_at: string | null;
    skills: string[];
    specializations: string[];
    formats: string[];
}

interface StandingPool {
    pool_id: string;
    gig_type_name: string;
    gig_type_slug: string;
    geographic_scope: string;
    thematic_scope: string;
    members: VolunteerMember[];
}

interface ConsoleStandingPoolsTableProps {
    lens: string;
    onSelectVolunteer?: (id: string) => void;
}

function initials(name: string): string {
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : (name[0] || "V").toUpperCase();
}

export default function ConsoleStandingPoolsTable({
    lens,
    onSelectVolunteer
}: ConsoleStandingPoolsTableProps) {
    const [pools, setPools] = useState<StandingPool[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedPoolId, setExpandedPoolId] = useState<string | null>(null);

    async function loadPools() {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/pools?lens=${lens}`);
            if (!res.ok) throw new Error("Failed to load standing pools.");
            const data = await res.json();
            setPools(data);
        } catch (err: any) {
            console.error("Error loading standing pools:", err);
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadPools();
    }, [lens]);

    const togglePool = (poolId: string) => {
        setExpandedPoolId(prev => (prev === poolId ? null : poolId));
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-gray-150 border-t-[#007a8a] rounded-full animate-spin mb-4" />
                <p className="text-sm text-gray-500 font-sans">Loading standing pools...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-xl mx-auto my-6">
                <h4 className="font-bold text-red-800 text-sm mb-1">Failed to Load Standing Pools</h4>
                <p className="text-xs text-red-600 mb-4">{error}</p>
                <button
                    onClick={loadPools}
                    className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded-lg font-sans font-bold text-xs uppercase tracking-wider hover:bg-red-100 transition-colors cursor-pointer"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (pools.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-gray-150 p-12 text-center shadow-sm">
                <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-gray-400" />
                </div>
                <h4 className="text-base font-bold text-[#003d45] mb-1">No Active Standing Pools</h4>
                <p className="text-xs text-gray-400 max-w-sm mx-auto leading-relaxed">
                    There are no standing availability pools matching the active lens filter or registered in the system.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {pools.map(pool => {
                const isExpanded = expandedPoolId === pool.pool_id;
                return (
                    <div
                        key={pool.pool_id}
                        className="bg-white rounded-xl border border-gray-150 shadow-sm overflow-hidden transition-all duration-200"
                    >
                        {/* Pool Summary Card */}
                        <div
                            onClick={() => togglePool(pool.pool_id)}
                            className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-base font-bold text-[#003d45] tracking-tight leading-snug">
                                        {pool.gig_type_name} Pool
                                    </h3>
                                    <span className="bg-[#007a8a]/10 text-[#007a8a] text-[10px] font-sans font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-[#007a8a]/20 shrink-0">
                                        Active
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-400 font-sans">
                                    {pool.geographic_scope && (
                                        <span>Geographic Scope: <strong className="text-gray-500 font-semibold">{pool.geographic_scope}</strong></span>
                                    )}
                                    {pool.thematic_scope && (
                                        <span>Focus: <strong className="text-gray-500 font-semibold">{pool.thematic_scope}</strong></span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-6 shrink-0 justify-between sm:justify-end">
                                <div className="text-left sm:text-right">
                                    <div className="text-xl font-black text-[#003d45] leading-none">
                                        {pool.members.length}
                                    </div>
                                    <div className="text-[9px] font-sans font-bold uppercase tracking-wider text-gray-400 mt-1">
                                        Volunteers
                                    </div>
                                </div>
                                <div className="text-gray-400 hover:text-gray-600 transition-colors">
                                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </div>
                            </div>
                        </div>

                        {/* Expanded Volunteers List */}
                        {isExpanded && (
                            <div className="border-t border-gray-100 bg-gray-50/30">
                                {pool.members.length === 0 ? (
                                    <div className="p-8 text-center text-xs text-gray-400 font-sans leading-relaxed">
                                        No volunteers have raised their hand to join this standing pool yet.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {pool.members.map(member => (
                                            <div
                                                key={member.volunteer_id}
                                                className="p-5 flex flex-col md:flex-row justify-between md:items-center gap-4 hover:bg-white transition-colors"
                                            >
                                                {/* Left Column: Volunteer Details */}
                                                <div className="flex items-start gap-3 min-w-0">
                                                    <div className="w-8 h-8 rounded-full bg-[#003d45] text-white text-xs font-black flex items-center justify-center uppercase shrink-0 mt-0.5">
                                                        {initials(member.name)}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <button
                                                                onClick={() => onSelectVolunteer && onSelectVolunteer(member.volunteer_id)}
                                                                className="text-sm font-bold text-[#003d45] text-left hover:text-[#007a8a] transition-colors focus:outline-none cursor-pointer"
                                                            >
                                                                {member.name}
                                                            </button>
                                                            <span className="flex items-center gap-1 text-[10px] text-gray-400 font-medium">
                                                                <Calendar className="w-3 h-3" />
                                                                Joined {member.opted_in_at ? new Date(member.opted_in_at).toLocaleDateString() : "—"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-400">
                                                            <Mail className="w-3 h-3 text-gray-300" />
                                                            <span className="truncate">{member.email}</span>
                                                        </div>

                                                        {/* Tags (Skills, Specs, Formats) */}
                                                        <div className="flex flex-wrap gap-1.5 mt-3">
                                                            {member.skills.map(skill => (
                                                                <span key={skill} className="inline-flex items-center gap-1 bg-[#007a8a]/5 text-[#007a8a] border border-[#007a8a]/10 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                                                                    <Tag className="w-2.5 h-2.5" />
                                                                    {skill}
                                                                </span>
                                                            ))}
                                                            {member.specializations.map(spec => (
                                                                <span key={spec} className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200/60 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                                                                    <Layers className="w-2.5 h-2.5" />
                                                                    {spec}
                                                                </span>
                                                            ))}
                                                            {member.formats.map(fmt => (
                                                                <span key={fmt} className="inline-flex items-center gap-1 bg-[#64748b]/5 text-[#64748b] border border-[#64748b]/10 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                                                                    <Monitor className="w-2.5 h-2.5" />
                                                                    {fmt}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right Column: Actions */}
                                                <div className="shrink-0 flex items-center justify-end">
                                                    <button
                                                        onClick={() => onSelectVolunteer && onSelectVolunteer(member.volunteer_id)}
                                                        className="bg-[#007a8a]/10 hover:bg-[#007a8a]/20 text-[#007a8a] border-none font-sans font-bold text-[10.5px] uppercase tracking-wider py-2 px-4 rounded-lg cursor-pointer transition-colors"
                                                    >
                                                        View Profile
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
